import * as DomBuilder from "./dom-builder.js";
import * as Utils from "./utils.js";
import Chart from 'chart.js/auto';
import Filters from "../Filters";

/**
 *
 * @version 2026-09-01
 * @author Patrik Harag
 */
export default class ComponentAnalysisChartCategories {

    static #DEFAULT_COLORS = [
        '#1b87c2',
        '#87058c',
        '#ad6607',
        '#0b1cc0',
        '#de3391',
        '#ffcc00',
        '#01a979',
    ];


    /** @type {Controller} */
    #controller;
    /** @type {Map<string,Transaction[]>} */
    #groupedTransactions;
    /** @type {Filter} */
    #selectedFilter;
    /** @type {Map<string,Filter>} */
    #allFilters;
    /** @type {HTMLElement} */
    #container = DomBuilder.div({ class: 'chart-panel' });

    /**
     *
     * @param controller {Controller}
     * @param groupedTransactions {Map<string,Transaction[]>}
     * @param allFilters {Map<string,Filter>}
     * @param selectedFilter {Filter}
     */
    constructor(controller, groupedTransactions, allFilters, selectedFilter) {
        this.#controller = controller;
        this.#groupedTransactions = groupedTransactions;
        this.#allFilters = allFilters;
        this.#selectedFilter = selectedFilter;
    }

    createNode() {
        return this.#container;
    }

    // chart's parent needs to be in the dom
    refresh() {
        if (this.#groupedTransactions.size > 3 && this.#groupedTransactions.size < 100) {
            this.#buildCashFlowChart(this.#groupedTransactions, this.#container);
        }
    }

    #buildCashFlowChart(groupedTransactions, parent) {
        // prepare data
        let keys = Array.from(groupedTransactions.keys()).sort();
        let data = keys.map(key => {
            let categories = Filters.groupByFilters(groupedTransactions.get(key), this.#allFilters, "Others");
            return {
                categories: categories,
                key: key
            };
        });

        // sum non-empty categories
        let nonEmptyCategories = new Set();
        let uniqueColors = new Set();
        data.forEach(data => {
            data.categories.forEach((value, key) => {
                if (value.transactions.length > 0) {
                    nonEmptyCategories.add(key);
                    uniqueColors.add((value.filter.getColor() != null) ? value.filter.getColor() : null);
                }
            });
        });

        let datasets = [];

        let randomColorBuffer = [...ComponentAnalysisChartCategories.#DEFAULT_COLORS];
        Filters.groupByFilters([], this.#allFilters, "Others").forEach((value, key) => {
            if (nonEmptyCategories.has(key)) {

                if (randomColorBuffer.length === 0) {
                    randomColorBuffer = [...ComponentAnalysisChartCategories.#DEFAULT_COLORS];
                }
                let color = randomColorBuffer.shift();
                if (value.filter.getColor() != null && !(uniqueColors.size === 1 && nonEmptyCategories.size > 1)) {
                    color = value.filter.getColor();
                }

                const [r, g, b] = Utils.parseHexColor(color);

                let dataset = {
                    label: value.filter.getName(),
                    data: [],
                    borderWidth: data.length < 15 ? 3 : 0,
                    borderColor: data.length < 15 ? color : undefined,
                    borderSkipped: data.length < 15 ? false : undefined,
                    backgroundColor: data.length < 15 ? `rgba(${r},${g},${b},0.2)` : `rgb(${r},${g},${b})`,
                    fill: true
                };

                datasets.push(dataset);
            }
        });

        data.forEach((value) => {
            let i = 0;
            let sum = 0;
            value.categories.forEach((category, key) => {
                if (!nonEmptyCategories.has(key)) {
                    // skip this one
                    return;
                }

                let result = category.transactions.reduce((sum, t) => sum + t.getValue(), 0);
                datasets[i].data.push(result);

                sum += result;
                i++;
            });
        });

        const config = {
            type: 'bar',
            data: {
                labels: keys,
                datasets: datasets
            },
            options: {
                responsive: true,
                plugins: {
                    title: {
                        display: true,
                        text: 'Categories'
                    },
                    legend: {
                        display: (datasets.length < 6)
                    }
                },
                scales: {
                    x: {
                        stacked: true,
                        title: {
                            display: true,
                            text: 'Time period'
                        }
                    },
                    y: {
                        stacked: true,
                        title: {
                            display: true,
                            text: 'Value'
                        }
                    }
                }
            }
        };

        let canvas = DomBuilder.element('canvas', { width: 400, height: 150 });
        parent.append(canvas);
        const chart = new Chart(canvas, config);
    }
}
